import breakpoints from '@/shared/styles/themeBreakpoints';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for efficiently detecting screen width and desktop status
 * Uses ResizeObserver for performance when available, falls back to resize events
 * Returns both screen width and desktop detection
 */
export const useScreenWidth = () => {
  const [screenWidth, setScreenWidth] = useState(() => {
    // Initialize with current screen width
    if (globalThis.window !== undefined) {
      return globalThis.window.innerWidth;
    }
    return 1200; // Default fallback
  });

  const updateScreenWidth = useCallback(() => {
    if (globalThis.window !== undefined) {
      setScreenWidth(globalThis.window.innerWidth);
    }
  }, []);

  useEffect(() => {
    if (globalThis.window === undefined) return;

    let resizeObserver: ResizeObserver | null = null;

    // Check if ResizeObserver is available
    if (typeof ResizeObserver !== 'undefined') {
      try {
        // Use ResizeObserver for better performance than resize event
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === document.documentElement || entry.target === document.body) {
              updateScreenWidth();
              break;
            }
          }
        });

        // Observe document element for size changes
        resizeObserver.observe(document.documentElement);
      } catch (error) {
        // If ResizeObserver fails, fall back to resize events
        console.warn('ResizeObserver failed, falling back to resize events..', error);
        resizeObserver = null;
      }
    }

    if (!resizeObserver) {
      // Fallback to resize event for older browsers
      globalThis.window.addEventListener('resize', updateScreenWidth);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        globalThis.window.removeEventListener('resize', updateScreenWidth);
      }
    };
  }, [updateScreenWidth]);

  // Calculate isDesktop based on screen width
  const isDesktop = screenWidth >= breakpoints.values.sm;
  const isTabletOrMobile = screenWidth < breakpoints.values.md;

  const isMobile = screenWidth < breakpoints.values.sm;

  return {
    screenWidth,
    isDesktop,
    isTabletOrMobile,
    isMobile,
  };
};
